import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Receipt, Signal, Zap, Droplet, Wifi, Check } from "lucide-react";

const ICONS = { signal: Signal, zap: Zap, droplet: Droplet, wifi: Wifi };

export default function BillsModal({ open, onClose, onSuccess }) {
  const { t, lang } = useApp();
  const [billers, setBillers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [account, setAccount] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/billers").then((r) => setBillers(r.data)).catch(() => {});
      setSelected(null); setAccount(""); setAmount("");
    }
  }, [open]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!selected || !account.trim() || !amt || amt <= 0) return;
    setLoading(true);
    try {
      await api.post("/bills/pay", { biller_id: selected.id, account_number: account.trim(), amount: amt });
      toast.success(selected.category === "telecom" ? t.toastRecharged : t.toastBillPaid);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="bills-modal" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-primary" /> {t.billsRecharge}
          </DialogTitle>
          <DialogDescription>{t.billsDesc}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.selectBiller}</label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {billers.map((b) => {
                const Icon = ICONS[b.icon] || Receipt;
                const active = selected?.id === b.id;
                return (
                  <button
                    key={b.id}
                    data-testid={`biller-${b.id}`}
                    onClick={() => setSelected(b)}
                    className={`relative flex flex-col items-center gap-1.5 p-2.5 rounded-2xl border transition-all ${active ? "border-primary bg-primary/10" : "border-border bg-card hover:bg-muted/50"}`}
                  >
                    {active && <Check className="w-3.5 h-3.5 text-primary absolute top-1.5 end-1.5" />}
                    <Icon className={`w-5 h-5 ${active ? "text-primary" : "text-foreground/70"}`} />
                    <span className="text-[11px] font-semibold text-center leading-tight">{lang === "ar" ? b.name_ar : b.name_en}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.accountNumber}</label>
            <Input
              data-testid="bill-account-input"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              placeholder={t.accountNumberPlaceholder}
              className="h-12 rounded-2xl mt-1.5 tabular-nums"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t.amount}</label>
            <div className="relative mt-1.5">
              <Input
                data-testid="bill-amount-input"
                type="number"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={t.enterAmount}
                className="h-12 rounded-2xl text-lg font-bold tabular-nums"
              />
              <span className="absolute top-1/2 -translate-y-1/2 end-4 text-sm font-bold text-muted-foreground">{t.currency}</span>
            </div>
          </div>

          <Button
            data-testid="bill-pay-submit"
            onClick={submit}
            disabled={loading || !selected || !account || !amount}
            className="w-full h-[52px] rounded-2xl text-base font-bold"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.pay}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
